<script setup lang="ts" generic="T extends {id: number}">
  import { vOnClickOutside } from '@vueuse/components'
  import { useEventListener } from '@vueuse/core'
  import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

  const props = defineProps<{
    items: T[],
    autoOpenOnItemsChange?: boolean, // whether to automatically open items dropdown when items list changed
  }>()

  const emit = defineEmits<{
    (e: 'select', item: T): void,
  }>()

  const selectItem = (item: T, index: number) => {
    emit('select', item)
    selectedIndex.value = index
  }

  const isItemsShown = ref(false)
  const selectedIndex = ref(-1)
  const slotContainer = useTemplateRef('slotContainer')
  const autocomplete = useTemplateRef('autocomplete')

  const showItems = () => {
    isItemsShown.value = true
    if (selectedIndex.value === -1) {
      selectedIndex.value = props.items.length > 0 ? 0 : -1
    } else {
      scrollIntoView()
    }
  }

  const hideItems = () => {
    isItemsShown.value = false
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (!isItemsShown.value) return // do nothing if items are not shown

    if (event.key === 'Enter') {
      event.preventDefault()
      if (selectedIndex.value >= 0 && props.items[selectedIndex.value]) {
        selectItem(props.items[selectedIndex.value]!, selectedIndex.value)
        hideItems()
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, props.items.length - 1)
    } else if (event.key === 'Escape') {
      hideItems()
    }
    scrollIntoView()
  }

  const scrollIntoView = () => {
    nextTick(() => { // wait until DOM updated, then scroll to selected item, make sure the item is always visible
      autocomplete.value?.querySelector('.selected')?.scrollIntoView({ block: 'nearest' })
    })
  }

  onMounted(() => {
    if (slotContainer.value) {
      // Find the first input element in the slot
      const inputElement = slotContainer.value.querySelector('input')

      if (inputElement) {
        useEventListener(inputElement, 'focus', () => {
          showItems()
        })
        useEventListener(inputElement, 'keydown', handleKeydown)
      }
    }
  })

  watch(() => props.items, (newItems) => {
    // items list changed, reset selected index
    if (props.autoOpenOnItemsChange ?? true) {
      isItemsShown.value = true
    }
    if (isItemsShown.value && newItems.length > 0) {
      selectedIndex.value = 0
    }
  })
</script>

<template>
  <div
    ref="autocomplete"
    class="relative"
    :class="{'z-10': isItemsShown}">
    <div
      ref="slotContainer"
      v-on-click-outside="hideItems">
      <!-- app input goes here -->
      <slot></slot>
    </div>

    <div
      v-if="isItemsShown && items.length > 0"
      class="autocomplete-items">
      <app-button
        v-for="(item, index) in items"
        :key="item.id"
        class="w-full hover:bg-gray-200 text-left px-4 py-2"
        :class="{
          'selected bg-blue-200 hover:bg-blue-200! border-blue-200': index === selectedIndex
        }"
        @click="selectItem(item, index)">
        <!-- item content goes here -->
        <slot
          name="items"
          :item="item"></slot>
      </app-button>
    </div>
  </div>
</template>

<style scoped>
    @reference "@/assets/styles/main.css";

  .autocomplete-items {
    @apply absolute inset-x-0 bg-white rounded-b border shadow overflow-y-auto max-h-80;
    @apply text-sm;
    @apply divide-y;
  }
</style>
